import { Controller } from "@hotwired/stimulus";

const SURPRISE_PROMPTS = [
	"A lone astronaut walks across a salt flat under twin moons, camera slowly pushes in from a wide establishing shot to a tight portrait. Soft ambient score, muted palette with pops of amber.",
	"Hyperlapse through the streets of New York at dusk, rain-slick pavement reflects neon, crowds blur into streaks of light, ending on a close-up of a coffee cup fogging up a diner window.",
	"Macro footage of a bee dusting pollen off a sunflower, slow motion, golden hour light, shallow depth of field, gentle strings building into a hopeful swell.",
	"A bento-box cooking tutorial shot top-down on a dark wooden board, hands moving with quiet precision, chalk title cards between steps, lo-fi beat underneath.",
	"A moody cyberpunk interior monologue: a detective reviews holographic evidence in a dim apartment, rain against the windows, flickering blue light, voice-over tinged with regret.",
];

export default class ComposerController extends Controller {
	static targets = [
		"script",
		"charCount",
		"durationSlider",
		"durationValue",
		"durationUnit",
		"durationInput",
		"costValue",
	];
	static values = { maxChars: { type: Number, default: 2000 } };

	connect() {
		this.updateChars();
		if (this.hasDurationSliderTarget) this.updateDuration();
	}

	updateChars() {
		if (!this.hasScriptTarget || !this.hasCharCountTarget) return;
		const len = this.scriptTarget.value.length;
		this.charCountTarget.textContent = `${len} / ${this.maxCharsValue}`;
		this.charCountTarget.classList.toggle(
			"text-rose-400",
			len > this.maxCharsValue,
		);
	}

	updateDuration() {
		if (!this.hasDurationSliderTarget) return;
		const seconds = Number.parseInt(this.durationSliderTarget.value, 10);
		if (this.hasDurationValueTarget) {
			this.durationValueTarget.textContent =
				seconds >= 60 ? this._formatMinutes(seconds) : String(seconds);
		}
		if (this.hasDurationUnitTarget) {
			this.durationUnitTarget.textContent = seconds >= 60 ? "" : "sec";
		}
		if (this.hasCostValueTarget) {
			this.costValueTarget.textContent = `~${this._formatMinutes(seconds)}`;
		}
		if (this.hasDurationInputTarget) {
			this.durationInputTarget.value = seconds;
		}
	}

	usePreset(event) {
		const prompt = event.params?.prompt;
		if (!prompt || !this.hasScriptTarget) return;
		this.scriptTarget.value = prompt;
		this.scriptTarget.focus();
		this.updateChars();
	}

	surprise() {
		if (!this.hasScriptTarget) return;
		const random =
			SURPRISE_PROMPTS[Math.floor(Math.random() * SURPRISE_PROMPTS.length)];
		this.scriptTarget.value = random;
		this.updateChars();
	}

	clear() {
		if (!this.hasScriptTarget) return;
		this.scriptTarget.value = "";
		this.scriptTarget.focus();
		this.updateChars();
	}

	_formatMinutes(seconds) {
		const hours = Math.floor(seconds / 3600);
		const mins = Math.floor((seconds % 3600) / 60);
		const rem = seconds % 60;
		if (hours > 0) {
			const minuteText = mins > 0 ? ` ${mins}m` : "";
			return `${hours}h${minuteText}`;
		}
		return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`;
	}
}
